/**
 * TASK-PBG-005: Bleed Bounds Calculator
 *
 * Calculates terrain bounds with bleed factor applied.
 * Bleed factor extends terrain beyond visible grid to prevent edge visibility when panning.
 *
 * @example
 * ```typescript
 * const gridBounds = { minX: 0, maxX: 1000, minY: 0, maxY: 800 };
 * const bleedBounds = calculateBleedBounds(gridBounds, 1.5);
 * // Returns: { minX: -250, maxX: 1250, minY: -200, maxY: 1000, width: 1500, height: 1200 }
 * ```
 */

/**
 * Grid bounds rectangle
 */
export interface GridBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Extended bounds with bleed applied
 */
export interface BleedBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
}

/**
 * Calculate terrain bounds with bleed factor applied
 *
 * The bleed factor extends the terrain beyond the grid bounds to ensure
 * camera panning never reveals the edge of the terrain. A factor of 1.5
 * means the terrain is 50% larger than the grid in each dimension.
 *
 * @param gridBounds - The original hex grid bounds
 * @param bleedFactor - Multiplier for terrain size (1.5 = 50% extra, 2.0 = 100% extra)
 * @returns Extended bounds with width and height calculated
 *
 * @example
 * ```typescript
 * // 100x100 grid with 50% bleed (1.5 factor)
 * const bounds = calculateBleedBounds(
 *   { minX: 0, maxX: 100, minY: 0, maxY: 100 },
 *   1.5
 * );
 * // Returns: { minX: -25, maxX: 125, minY: -25, maxY: 125, width: 150, height: 150 }
 * ```
 */
export function calculateBleedBounds(
  gridBounds: GridBounds,
  bleedFactor: number
): BleedBounds {
  // Calculate original grid dimensions
  const gridWidth = gridBounds.maxX - gridBounds.minX;
  const gridHeight = gridBounds.maxY - gridBounds.minY;

  // Calculate new dimensions with bleed factor applied
  const bleedWidth = gridWidth * bleedFactor;
  const bleedHeight = gridHeight * bleedFactor;

  // Calculate grid center point
  const centerX = gridBounds.minX + gridWidth / 2;
  const centerY = gridBounds.minY + gridHeight / 2;

  // Calculate extended bounds centered on grid
  const halfBleedWidth = bleedWidth / 2;
  const halfBleedHeight = bleedHeight / 2;

  return {
    minX: centerX - halfBleedWidth,
    maxX: centerX + halfBleedWidth,
    minY: centerY - halfBleedHeight,
    maxY: centerY + halfBleedHeight,
    width: bleedWidth,
    height: bleedHeight,
  };
}
